/**
 * TenantLink — react-router's Link with tenant context preserved
 *
 * @module lib/navigation/TenantLink
 */

import { forwardRef, useMemo } from 'react';
import {
  Link as RouterLink,
  type LinkProps as RouterLinkProps,
  useSearchParams,
} from 'react-router';
import { buildUrlWithCurrentParams } from './utils';

/**
 * Link that carries the current tenant context onto the target URL
 *
 * Wraps React Router's Link and copies `tenant-handle` and `club-id` (the
 * `PRESERVED_QUERY_PARAMS` allowlist) from the current URL. Nothing else is
 * carried — page-local params stay on the page that set them.
 *
 * Named apart from react-router's `Link` on purpose: a file should say which
 * one it uses. Import it as `TenantLink`, never aliased back to `Link`.
 *
 * @param preserveParams - If false, skips automatic param preservation (default: true)
 *
 * @example
 * ```tsx
 * // Automatically preserves tenant-handle and club-id
 * <TenantLink to="/booking/confirmed/185">View Booking</TenantLink>
 *
 * // Opt out of param preservation
 * <TenantLink to="/home" preserveParams={false}>Home</TenantLink>
 *
 * // Explicit params merge with the preserved ones (and win over them)
 * <TenantLink to="/booking/confirmed/185?payment-provider=square">
 *   View Booking
 * </TenantLink>
 * ```
 */
export interface TenantLinkProps extends Omit<RouterLinkProps, 'to'> {
  to: string;
  preserveParams?: boolean;
}

export const TenantLink = forwardRef<HTMLAnchorElement, TenantLinkProps>(
  ({ to, preserveParams = true, ...props }, ref) => {
    const [searchParams] = useSearchParams();

    // Memoize the final URL to avoid rebuilding on every render
    const finalUrl = useMemo(() => {
      if (!preserveParams) return to;
      return buildUrlWithCurrentParams(to, searchParams);
    }, [to, preserveParams, searchParams]);

    return <RouterLink ref={ref} to={finalUrl} {...props} />;
  }
);

TenantLink.displayName = 'TenantLink';
